import { useEffect, useState, type ReactElement } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import {
  AlertTriangle,
  Clock,
  Edit2,
  Globe,
  Layers,
  LifeBuoy,
  Mail,
  MessageSquare,
  Paperclip,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  User,
  UserCheck,
  UserX,
  X,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/loading";
import { PaginationControls } from "@/components/PaginationControls";
import { useStaffTickets } from "@/hooks/use-tickets";
import { useDeleteTicketTopic, useTicketTopics } from "@/hooks/use-ticket-topics";
import {
  TicketPriority,
  TicketStatus,
  ticketErrorMessage,
} from "@/lib/api/tickets";
import {
  TicketAssigneeRole,
  type TicketTopic,
} from "@/lib/api/ticket-topics";
import { TicketStatusBadge } from "@/components/tickets/ticket-status-badge";
import { TicketPriorityBadge } from "@/components/tickets/ticket-priority-badge";
import { TicketSlaBadge } from "@/components/tickets/ticket-sla-badge";
import { useNow } from "@/hooks/use-now";
import { StaffTicketDetailDialog } from "@/components/tickets/staff-ticket-detail-dialog";
import { AdminTopicDialog } from "@/components/tickets/admin-topic-dialog";
import { cn } from "@/lib/utils";

interface AdminTicketsSearch {
  tab?: "board" | "topics";
  status?: string;
  priority?: string;
  topicId?: string;
  search?: string;
  page?: number;
  unassigned?: boolean;
  breached?: boolean;
  /** Set by a notification deep link (/tickets/{id}); opens that ticket on arrival. */
  ticketId?: string;
}

export const Route = createFileRoute("/_authenticated/admin/tickets")({
  validateSearch: (search: Record<string, unknown>): AdminTicketsSearch => ({
    tab: search.tab === "topics" ? "topics" : "board",
    status: typeof search.status === "string" ? search.status : undefined,
    priority: typeof search.priority === "string" ? search.priority : undefined,
    topicId: typeof search.topicId === "string" ? search.topicId : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
    page:
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? parseInt(search.page, 10) || undefined
          : undefined,
    unassigned: search.unassigned === true || search.unassigned === "true",
    breached: search.breached === true || search.breached === "true",
    ticketId: typeof search.ticketId === "string" ? search.ticketId : undefined,
  }),
  component: AdminTicketsPage,
});

const STATUS_FILTERS: Array<{ key: string; label: string; status?: TicketStatus }> = [
  { key: "all", label: "All Tickets" },
  { key: "open", label: "Open", status: TicketStatus.Open },
  { key: "in_progress", label: "In Progress", status: TicketStatus.InProgress },
  { key: "resolved", label: "Resolved", status: TicketStatus.Resolved },
  { key: "closed", label: "Closed", status: TicketStatus.Closed },
];

function AdminTicketsPage(): ReactElement {
  const { isAdmin, isSuperAdmin, hasPermission } = useAuth();

  if (!isAdmin && !isSuperAdmin && !hasPermission("manage:users")) {
    return (
      <AccessDenied message="You do not have administrative permission to view the global support ticketing board." />
    );
  }

  return <AdminTicketsContent />;
}

function AdminTicketsContent(): ReactElement {
  const searchParams = Route.useSearch() as AdminTicketsSearch;
  const navigate = useNavigate({ from: Route.fullPath });

  const activeMainTab = searchParams.tab ?? "board";

  // One clock for every SLA badge on this page — see useNow.
  const now = useNow(15_000);

  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Deep links arrive as ?ticketId=… from /tickets/{id}, which is where every staff notification
  // about a ticket points. Driven by an effect rather than by seeding the initial state, because
  // the route stays mounted when only the search changes — acting on two notifications in a row
  // would otherwise leave the first ticket on screen.
  useEffect(() => {
    if (!searchParams.ticketId) return;
    setSelectedTicketId(searchParams.ticketId);
    setIsDetailOpen(true);
  }, [searchParams.ticketId]);

  const [selectedTopic, setSelectedTopic] = useState<TicketTopic | null>(null);
  const [isTopicDialogOpen, setIsTopicDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<TicketTopic | null>(null);

  const [searchInput, setSearchInput] = useState(searchParams.search ?? "");

  const activeStatusKey = searchParams.status ?? "all";
  const activeConfig =
    STATUS_FILTERS.find((filter) => filter.key === activeStatusKey) ?? STATUS_FILTERS[0];
  const targetStatus = activeConfig.status;

  const targetPriority =
    searchParams.priority !== undefined && searchParams.priority !== "all"
      ? (parseInt(searchParams.priority, 10) as TicketPriority)
      : undefined;

  const pageNumber = searchParams.page && searchParams.page > 0 ? searchParams.page : 1;

  const {
    data: topicsData,
    isLoading: isLoadingTopics,
    refetch: refetchTopics,
  } = useTicketTopics(true);

  const {
    data: ticketsData,
    isLoading: isLoadingTickets,
    isFetching: isFetchingTickets,
    isError: isTicketsError,
    refetch: refetchTickets,
  } = useStaffTickets({
    status: targetStatus,
    priority: targetPriority,
    topicId: searchParams.topicId,
    unassignedOnly: searchParams.unassigned,
    breachedOnly: searchParams.breached,
    search: searchParams.search,
    pageNumber,
    pageSize: 20,
  });

  const deleteTopicMutation = useDeleteTicketTopic();

  function updateSearch(updates: Partial<AdminTicketsSearch>) {
    void navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...updates,
        page: updates.page !== undefined ? updates.page : undefined,
      }),
    });
  }

  function handleMainTabChange(tabKey: string) {
    updateSearch({ tab: tabKey as "board" | "topics" });
  }

  function handleStatusTabChange(tabKey: string) {
    updateSearch({
      status: tabKey === "all" ? undefined : tabKey,
      page: undefined,
    });
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateSearch({
      search: searchInput.trim() || undefined,
      page: undefined,
    });
  }

  function handleClearSearch() {
    setSearchInput("");
    updateSearch({
      search: undefined,
      page: undefined,
    });
  }

  function handleOpenTicket(ticketId: string) {
    setSelectedTicketId(ticketId);
    setIsDetailOpen(true);
  }

  function handleCreateTopic() {
    setSelectedTopic(null);
    setIsTopicDialogOpen(true);
  }

  function handleEditTopic(topic: TicketTopic) {
    setSelectedTopic(topic);
    setIsTopicDialogOpen(true);
  }

  function handleDeleteTopicConfirm() {
    if (!topicToDelete) return;

    deleteTopicMutation.mutate(topicToDelete.id, {
      onSuccess: () => {
        toast.success("Topic deleted successfully.");
        setTopicToDelete(null);
      },
      onError: (err) => {
        toast.error(
          ticketErrorMessage(
            err,
            "Failed to delete topic. Ensure no tickets are referencing this topic.",
          ),
        );
      },
    });
  }

  const tickets = ticketsData?.items ?? [];
  const topics = topicsData ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-7xl mx-auto">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground">
            <LifeBuoy className="h-6 w-6 text-primary shrink-0" aria-hidden="true" />
            Support Ticketing Center
          </h1>
          <p className="text-sm text-muted-foreground">
            Global management board for all student support requests, SLA monitoring, and dynamic support topics.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-start">
          {activeMainTab === "topics" ? (
            <Button onClick={handleCreateTopic} className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span>New Topic</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchTickets()}
              disabled={isFetchingTickets}
              className="gap-1.5"
            >
              <RefreshCw className={cn("h-4 w-4", isFetchingTickets && "animate-spin")} aria-hidden="true" />
              <span>Refresh</span>
            </Button>
          )}
        </div>
      </header>

      <Tabs value={activeMainTab} onValueChange={handleMainTabChange} className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-xs mb-4">
          <TabsTrigger value="board" className="gap-1.5 text-xs sm:text-sm">
            <Globe className="h-4 w-4" aria-hidden="true" />
            <span>Global Board</span>
          </TabsTrigger>
          <TabsTrigger value="topics" className="gap-1.5 text-xs sm:text-sm">
            <Layers className="h-4 w-4" aria-hidden="true" />
            <span>Support Topics ({topics.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: GLOBAL TICKETS BOARD */}
        <TabsContent value="board" className="space-y-4 mt-0">
          <Tabs value={activeStatusKey} onValueChange={handleStatusTabChange} className="w-full">
            <TabsList className="grid grid-cols-5 w-full max-w-xl">
              {STATUS_FILTERS.map((filter) => (
                <TabsTrigger key={filter.key} value={filter.key} className="text-xs sm:text-sm">
                  {filter.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Filter Toolbar */}
          <div className="flex flex-wrap items-center gap-2.5 p-3 rounded-lg border bg-card shadow-xs">
            <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search subject..."
                className="pl-8 pr-7 h-8 text-xs"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </form>

            <div className="w-40">
              <Select
                value={searchParams.priority ?? "all"}
                onValueChange={(val) =>
                  updateSearch({ priority: val === "all" ? undefined : val, page: undefined })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value={TicketPriority.Critical.toString()}>Critical</SelectItem>
                  <SelectItem value={TicketPriority.High.toString()}>High</SelectItem>
                  <SelectItem value={TicketPriority.Normal.toString()}>Normal</SelectItem>
                  <SelectItem value={TicketPriority.Low.toString()}>Low</SelectItem>
                  <SelectItem value={TicketPriority.Escalated.toString()}>Escalated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-44">
              <Select
                value={searchParams.topicId ?? "all"}
                onValueChange={(val) =>
                  updateSearch({ topicId: val === "all" ? undefined : val, page: undefined })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="All Topics" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  {topics.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id}>
                      {topic.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant={searchParams.unassigned ? "default" : "outline"}
              size="sm"
              onClick={() =>
                updateSearch({
                  unassigned: !searchParams.unassigned || undefined,
                  page: undefined,
                })
              }
              className="h-8 text-xs gap-1"
            >
              <UserX className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Unassigned</span>
            </Button>

            <Button
              variant={searchParams.breached ? "destructive" : "outline"}
              size="sm"
              onClick={() =>
                updateSearch({
                  breached: !searchParams.breached || undefined,
                  page: undefined,
                })
              }
              className="h-8 text-xs gap-1"
            >
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Breached SLA</span>
            </Button>
          </div>

          {/* Tickets Table */}
          {isTicketsError ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-12 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Could not load tickets.</p>
              <Button variant="outline" size="sm" onClick={() => refetchTickets()}>
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Retry
              </Button>
            </div>
          ) : isLoadingTickets ? (
            <TableSkeleton rows={5} />
          ) : tickets.length === 0 ? (
            <EmptyState
              icon={LifeBuoy}
              title="No tickets found"
              description="No tickets match the selected filters on the global board."
            />
          ) : (
            <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[28%]">Ticket & Student</TableHead>
                    <TableHead className="w-[14%]">Topic</TableHead>
                    <TableHead className="w-[10%]">Status</TableHead>
                    <TableHead className="w-[10%]">Priority</TableHead>
                    <TableHead className="w-[18%]">SLA Countdowns</TableHead>
                    <TableHead className="w-[12%]">Assignee</TableHead>
                    <TableHead className="w-[8%] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => {
                    const hasAttachment = Boolean(
                      (ticket as unknown as { attachment?: unknown; hasAttachment?: boolean }).attachment ||
                        (ticket as unknown as { hasAttachment?: boolean }).hasAttachment,
                    );
                    const lastActivityDate = new Date(ticket.lastActivityAt);
                    const isValidDate = !Number.isNaN(lastActivityDate.getTime());
                    const relativeUpdated = isValidDate
                      ? formatDistanceToNow(lastActivityDate, { addSuffix: true })
                      : "recently";

                    return (
                      <TableRow
                        key={ticket.id}
                        className="cursor-pointer hover:bg-muted/40 transition-colors"
                        onClick={() => handleOpenTicket(ticket.id)}
                      >
                        <TableCell className="align-top py-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-foreground text-sm hover:text-primary transition-colors line-clamp-1">
                                {ticket.subject}
                              </span>
                              {hasAttachment && (
                                <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" aria-hidden="true" />
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                              <User className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="font-medium text-foreground/80">{ticket.studentFullName || "Student"}</span>
                              {ticket.studentEmail && (
                                <>
                                  <span>•</span>
                                  <span className="text-[11px] truncate max-w-[140px]">{ticket.studentEmail}</span>
                                </>
                              )}
                              <span>•</span>
                              <span className="text-[11px]">Updated {relativeUpdated}</span>
                              <span>•</span>
                              <span className="inline-flex items-center gap-0.5 text-[11px]">
                                <MessageSquare className="h-2.5 w-2.5" aria-hidden="true" />
                                {ticket.messageCount}
                              </span>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell className="align-top py-3 text-xs font-medium text-foreground/90">
                          {ticket.topicTitle}
                        </TableCell>

                        <TableCell className="align-top py-3">
                          <TicketStatusBadge status={ticket.status} />
                        </TableCell>

                        <TableCell className="align-top py-3">
                          <TicketPriorityBadge priority={ticket.priority} />
                        </TableCell>

                        <TableCell className="align-top py-3">
                          <div className="flex flex-col gap-1">
                            <TicketSlaBadge ticket={ticket} type="first-response" now={now} />
                            <TicketSlaBadge ticket={ticket} type="resolution" now={now} />
                          </div>
                        </TableCell>

                        <TableCell className="align-top py-3 text-xs text-muted-foreground">
                          {ticket.assignedToFullName ? (
                            <span className="font-medium text-foreground/80">{ticket.assignedToFullName}</span>
                          ) : (
                            <span className="italic text-muted-foreground">Unassigned</span>
                          )}
                        </TableCell>

                        <TableCell className="align-top py-3 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTicket(ticket.id);
                            }}
                            className="h-7 text-xs"
                          >
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {ticketsData && ticketsData.totalPages > 1 && (
                <div className="p-3 border-t">
                  <PaginationControls
                    pageNumber={ticketsData.pageNumber}
                    pageSize={ticketsData.pageSize}
                    totalPages={ticketsData.totalPages}
                    totalCount={ticketsData.totalCount}
                    hasNextPage={ticketsData.hasNextPage}
                    hasPreviousPage={ticketsData.hasPreviousPage}
                    onPageChange={(page) => updateSearch({ page })}
                    onPageSizeChange={() => {}}
                    isLoading={isFetchingTickets}
                  />
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* TAB 2: SUPPORT TOPICS CRUD */}
        <TabsContent value="topics" className="space-y-4 mt-0">
          {isLoadingTopics ? (
            <TableSkeleton rows={4} />
          ) : topics.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="No support topics configured"
              description="Create support topics to allow students to submit structured support requests."
              action={
                <Button onClick={handleCreateTopic} className="gap-1.5">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <span>New Topic</span>
                </Button>
              }
            />
          ) : (
            <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[24%]">Topic</TableHead>
                    <TableHead className="w-[12%]">Default Priority</TableHead>
                    <TableHead className="w-[14%]">Default Routing</TableHead>
                    <TableHead className="w-[18%]">SLA Thresholds</TableHead>
                    <TableHead className="w-[16%]">Audience Targeting</TableHead>
                    <TableHead className="w-[8%]">Status</TableHead>
                    <TableHead className="w-[8%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topics.map((topic) => {
                    const isPlatformWide =
                      (!topic.batchIds || topic.batchIds.length === 0) &&
                      (!topic.groupIds || topic.groupIds.length === 0);

                    return (
                      <TableRow key={topic.id}>
                        <TableCell className="align-top py-3">
                          <div className="space-y-1">
                            <span className="font-semibold text-foreground text-sm">
                              {topic.title}
                            </span>
                            {topic.description && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {topic.description}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="align-top py-3">
                          <TicketPriorityBadge priority={topic.defaultPriority} />
                        </TableCell>

                        <TableCell className="align-top py-3 text-xs text-muted-foreground">
                          {topic.defaultAssigneeRole === TicketAssigneeRole.TeamLeader
                            ? "Team Leader"
                            : "Tech Support (Admin)"}
                        </TableCell>

                        <TableCell className="align-top py-3 text-xs text-muted-foreground space-y-1">
                          <div>
                            <span className="font-medium text-foreground">1st Response: </span>
                            {topic.firstResponseSlaMinutes
                              ? `${topic.firstResponseSlaMinutes} min`
                              : "Priority default"}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Resolution: </span>
                            {topic.resolutionSlaMinutes
                              ? `${topic.resolutionSlaMinutes} min`
                              : "Priority default"}
                          </div>
                        </TableCell>

                        <TableCell className="align-top py-3 text-xs">
                          {isPlatformWide ? (
                            <Badge variant="outline" className="font-normal text-muted-foreground">
                              Platform-Wide
                            </Badge>
                          ) : (
                            <div className="space-y-1 text-muted-foreground text-xs">
                              {topic.batchIds.length > 0 && (
                                <div>
                                  <span className="font-semibold text-foreground">
                                    {topic.batchIds.length}
                                  </span>{" "}
                                  batch(es)
                                </div>
                              )}
                              {topic.groupIds.length > 0 && (
                                <div>
                                  <span className="font-semibold text-foreground">
                                    {topic.groupIds.length}
                                  </span>{" "}
                                  group(s)
                                </div>
                              )}
                            </div>
                          )}
                        </TableCell>

                        <TableCell className="align-top py-3">
                          <Badge
                            variant={topic.isActive ? "default" : "outline"}
                            className={cn(
                              "text-xs",
                              topic.isActive
                                ? "bg-emerald-600 text-white"
                                : "text-muted-foreground border-muted-foreground/30",
                            )}
                          >
                            {topic.isActive ? "Active" : "Disabled"}
                          </Badge>
                        </TableCell>

                        <TableCell className="align-top py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditTopic(topic)}
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              title="Edit Topic"
                            >
                              <Edit2 className="h-3.5 w-3.5" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setTopicToDelete(topic)}
                              className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              title="Delete Topic"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Staff ticket detail dialog */}
      <StaffTicketDetailDialog
        ticketId={selectedTicketId}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
      />

      {/* Admin topic create/edit dialog */}
      <AdminTopicDialog
        topic={selectedTopic}
        open={isTopicDialogOpen}
        onOpenChange={setIsTopicDialogOpen}
      />

      {/* Delete Topic Confirmation Alert */}
      <AlertDialog
        open={Boolean(topicToDelete)}
        onOpenChange={(open) => !open && setTopicToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Support Topic</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the topic &quot;{topicToDelete?.title}&quot;?
              <br />
              <span className="text-xs text-muted-foreground mt-2 block">
                Topics that have any existing support tickets cannot be deleted and should instead be set to Disabled.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTopicConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Topic
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
